import { useCallback, useEffect, useMemo, useRef } from "react";

import type { VttSegment } from "./useTranscript";

interface UseAutoScrollOptions {
  currentTime: number;
  segments: VttSegment[];
  isAutoScrolling: boolean;
  isAutoScrollEnabled: boolean;
  onUserScroll?: () => void;
  refreshTrigger?: { value: string; timeout: number };
}

interface UseAutoScrollResult {
  activeSegmentId: string | null;
  scrollToSegment: (segmentId: string) => void;
  handleUserScroll: () => void;
}

export const useAutoScroll = ({
  currentTime,
  segments,
  isAutoScrolling,
  isAutoScrollEnabled,
  onUserScroll,
  refreshTrigger,
}: UseAutoScrollOptions): UseAutoScrollResult => {
  const scrollTimeoutRef = useRef<NodeJS.Timeout>(null);
  const lastAutoScrollTime = useRef<number>(0);
  const userInteractingRef = useRef(false);
  const prevRefreshTriggerRef = useRef<unknown>(null);
  const hasScrolledOnMount = useRef(false);
  const { value: refreshTriggerValue, timeout: refreshTriggerTimeout } =
    refreshTrigger ?? {};

  // Detect manual scrolling
  useEffect(() => {
    const setUser = () => {
      userInteractingRef.current = true;
      setTimeout(() => (userInteractingRef.current = false), 500);
    };
    window.addEventListener("mousedown", setUser);
    window.addEventListener("touchstart", setUser);
    window.addEventListener("wheel", setUser);
    window.addEventListener("keydown", setUser);

    return () => {
      window.removeEventListener("mousedown", setUser);
      window.removeEventListener("touchstart", setUser);
      window.removeEventListener("wheel", setUser);
      window.removeEventListener("keydown", setUser);
    };
  }, []);

  const activeSegmentId = useMemo(() => {
    if (!segments.length) return null;
    return segments.find(
      (segment) =>
        currentTime >= segment.startTime && currentTime <= segment.endTime,
    )?.id;
  }, [currentTime, segments]);

  const scrollToSegment = useCallback((segmentId: string) => {
    const element = document.getElementById(segmentId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, []);

  // Scroll to current segment on mount
  useEffect(() => {
    if (!isAutoScrollEnabled || !activeSegmentId || hasScrolledOnMount.current)
      return;

    if (activeSegmentId) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        scrollToSegment(activeSegmentId);
        hasScrolledOnMount.current = true;
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isAutoScrollEnabled, activeSegmentId]);

  // Auto-scroll to active segment
  useEffect(() => {
    if (!isAutoScrolling || !activeSegmentId || !isAutoScrollEnabled) return;

    // Prevent rapid scrolling by adding a small delay
    const now = Date.now();
    if (now - lastAutoScrollTime.current < 200) return;

    scrollToSegment(activeSegmentId);
    lastAutoScrollTime.current = now;
  }, [activeSegmentId, isAutoScrolling, isAutoScrollEnabled, scrollToSegment]);

  // Handle refresh trigger changes
  useEffect(() => {
    if (!isAutoScrolling || !activeSegmentId || refreshTrigger === undefined)
      return;

    const hasChanged = prevRefreshTriggerRef.current !== refreshTriggerValue;
    prevRefreshTriggerRef.current = refreshTriggerValue;

    if (!hasChanged) return;

    const timer = setTimeout(() => {
      const element = document.getElementById(activeSegmentId);
      if (element) {
        element.offsetHeight;
        scrollToSegment(activeSegmentId);
      }
    }, refreshTriggerTimeout);

    return () => clearTimeout(timer);
  }, [
    refreshTriggerValue,
    isAutoScrolling,
    activeSegmentId,
    scrollToSegment,
    refreshTriggerTimeout,
  ]);

  const handleUserScroll = useCallback(() => {
    if (userInteractingRef.current && isAutoScrolling) {
      onUserScroll?.();

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    }
  }, [onUserScroll, isAutoScrolling]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return {
    activeSegmentId,
    scrollToSegment,
    handleUserScroll,
  };
};
